import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { FormattedMessage as FM, injectIntl } from 'react-intl';
import queryString from 'query-string';
import { withTheme } from 'common/styling/theme';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { Tooltip } from 'common/components';
import { BasicCRMModal, CustomFilterInfo } from 'crm/_components';
import TooltipIcon from 'welcome-setup/_components/icons/TooltipIcon';

import { getCRMFilter } from 'crm/_api/api';

import CustomFilterButton from '../CustomFilterButton';
import iconBasket from './images/iconBasket.svg';
import iconCross from './images/iconCross.svg';

import { staticStyles, getDynamicStyles } from './style';

const CustomFilterSelect = ({
  getFilters,
  filtersSelector,
  modifiedFiltersSelector,
  onChange,
  onResetFilter,
  filter,
  filterId,
  toggleDeleteModal,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const selectRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFilters.request());
  }, [dispatch]);
  const filters = useSelector(filtersSelector);
  const [currentFilter, setCurrentFilter] = useState();
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const modifiedFilters = useSelector(modifiedFiltersSelector);
  const params = queryString.parse(window.location.search.replace('?', ''));
  const isCreateOptionDisabled = Object.keys(modifiedFilters).some(
    filterName => params[modifiedFilters[filterName].query_param]
  );

  const options = [
    ...filters.map(item => ({ value: item.id, label: item.title })),
    {
      value: 'create',
      isDisabled: !isCreateOptionDisabled,
      label: (
        <>
          {!isCreateOptionDisabled ? (
            <Tooltip textId="crmCreateFilterHelpText">
              <FM id="crmCreateAndEdit" />
            </Tooltip>
          ) : (
            <FM id="crmCreateAndEdit" />
          )}
        </>
      ),
    },
  ];
  const initialFilter = filterId ? options.find(option => option.value === +filterId) : filter;

  const handleToggleSelect = () => {
    if (!isFocused) {
      selectRef.current.focus();
    }

    setIsFocused(prev => !prev);
  };

  const handleToggleModal = () => setIsInfoOpen(isInfoOpen => !isInfoOpen);

  const handleGetFilterInfo = async id => {
    const { data } = await getCRMFilter(id);
    setCurrentFilter(data);

    handleToggleModal();
  };

  const Option = props => {
    if (props.data.value !== 'create') {
      return (
        <div
          className={classNames(
            { 'CustomFilterSelect__option--selected': props.isSelected },
            { 'CustomFilterSelect__option--focused': props.isFocused },
            'CustomFilterSelect__option'
          )}>
          <div className="CustomFilterSelect__option-text">
            <components.Option {...props} />
          </div>
          <div className="CustomFilterSelect__option-buttons">
            <button
              className="CustomFilterSelect__option-tooltip"
              onClick={() => {
                handleGetFilterInfo(props.data.value);
              }}>
              <TooltipIcon />
            </button>
            <button
              className="CustomFilterSelect__option-edit"
              onClick={e => {
                e.stopPropagation();
                toggleDeleteModal(props.data.value);
              }}>
              <img className="CustomFilterSelect__delete-icon" src={iconBasket} alt="edit" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="CustomFilterSelect__option-text CustomFilterSelect__option-text--whole">
        <components.Option {...props} />
      </div>
    );
  };

  return (
    <div className="CustomFilterSelect">
      <div className="CustomFilterSelect__button">
        <CustomFilterButton onClick={() => handleToggleSelect()} />
      </div>
      <div className="CustomFilterSelect__inner">
        <Select
          classNamePrefix="Select"
          placeholder={<FM id="crmCustomFilter" />}
          onChange={onChange}
          options={options}
          ref={selectRef}
          noOptionsMessage={() => null}
          isClearable={false}
          isSearchable={false}
          openMenuOnFocus
          components={{ Option }}
          value={filter.value ? { value: filter.value, label: filter.label } : initialFilter}
        />
        {filterId && (
          <button onClick={onResetFilter}>
            <img className="CustomFilterSelect__delete-icon" src={iconCross} alt="reset" />
          </button>
        )}
      </div>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
      {currentFilter && (
        <BasicCRMModal
          captionId={currentFilter.title}
          isOpen={isInfoOpen}
          onRequestClose={handleToggleModal}
          className="CustomFilterInfo__Modal">
          <CustomFilterInfo modifiedFiltersSelector={modifiedFiltersSelector} currentFilter={currentFilter} />
        </BasicCRMModal>
      )}
    </div>
  );
};

CustomFilterSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  onResetFilter: PropTypes.func.isRequired,
  toggleDeleteModal: PropTypes.func.isRequired,
  modifiedFiltersSelector: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  theme: PropTypes.object,
};

CustomFilterSelect.defaultProps = {
  theme: {},
};

export default withTheme()(injectIntl(CustomFilterSelect));
